import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge } from "@/components/common";
import { LinkDialog } from "@/components/LinkDialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { UserPlus, Trash2, Pencil, Plus, X, MapPinned, Mail, KeyRound } from "lucide-react";
import { toast } from "sonner";

const ANY = "__any__";
const EMPTY = [{ location_id: "", department_id: "" }];

function ScopeEditor({ scope, setScope, masters }) {
  const upd = (i, k, v) => setScope(scope.map((s, j) => (j === i ? { ...s, [k]: v === ANY ? "" : v } : s)));
  const pick = (i, k, items, label) => (
    <Select value={scope[i][k] || ANY} onValueChange={(v) => upd(i, k, v)}>
      <SelectTrigger className="h-9" data-testid={`scope-${i}-${k}`}><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value={ANY}>Any {label}</SelectItem>
        {items.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );
  return (
    <div className="space-y-2">
      {scope.map((_, i) => (
        <div key={i} className="grid grid-cols-[1fr_1fr_auto] items-center gap-2">
          {pick(i, "location_id", masters.locations, "location")}
          {pick(i, "department_id", masters.departments, "department")}
          <Button size="icon" variant="ghost" disabled={scope.length === 1} onClick={() => setScope(scope.filter((__, j) => j !== i))} data-testid={`scope-remove-${i}`}><X className="h-4 w-4" /></Button>
        </div>
      ))}
      <Button size="sm" variant="outline" onClick={() => setScope([...scope, { location_id: "", department_id: "" }])} data-testid="scope-add-row"><Plus className="mr-1 h-4 w-4" /> Add another</Button>
      <p className="text-xs text-slate-400">Pick a location, a department, or both on a row. The manager sees employees that match any row.</p>
    </div>
  );
}

const scopeValid = (scope) => scope.some((s) => s.location_id || s.department_id);

export default function CenterManagers() {
  const { actingOrgId } = useAuth();
  const qc = useQueryClient();
  const [dialog, setDialog] = useState(null);
  const [email, setEmail] = useState("");
  const [scope, setScope] = useState(EMPTY);
  const [link, setLink] = useState("");
  const [confirm, setConfirm] = useState(null);
  const [linkTitle, setLinkTitle] = useState("Link");

  const { data } = useQuery({ queryKey: ["center-managers", actingOrgId], queryFn: async () => (await api.get("/org/center-managers")).data });
  const { data: masters = { locations: [], departments: [] } } = useQuery({
    queryKey: ["cm-masters", actingOrgId],
    queryFn: async () => {
      const [l, d] = await Promise.all([api.get("/org/masters/locations"), api.get("/org/masters/departments")]);
      return { locations: l.data, departments: d.data };
    },
  });
  const name = (list, id) => list.find((x) => x.id === id)?.name;
  const scopeLabel = (s) => [name(masters.locations, s.location_id), name(masters.departments, s.department_id)].filter(Boolean).join(" · ");

  useEffect(() => {
    if (dialog === "invite") { setEmail(""); setScope(EMPTY); }
    if (dialog?.membership_id || dialog?.invite_id) { setScope(dialog.scope?.length ? dialog.scope.map((s) => ({ location_id: s.location_id || "", department_id: s.department_id || "" })) : EMPTY); }
  }, [dialog]);

  const done = (msg) => { toast.success(msg); setDialog(null); qc.invalidateQueries({ queryKey: ["center-managers"] }); };
  const onErr = (e) => toast.error(apiError(e.response?.data?.detail));
  const invite = useMutation({
    mutationFn: async () => (await api.post("/org/center-managers/invite", { email, scope })).data,
    onSuccess: (d) => { done(d.sent_status === "sent" ? "Invite emailed" : "Invite created (email not sent)"); setLink(d.link); },
    onError: onErr,
  });
  const save = useMutation({
    mutationFn: async () => (await (dialog.invite_id ? api.put(`/org/center-managers/invites/${dialog.invite_id}`, { scope }) : api.put(`/org/center-managers/${dialog.membership_id}`, { scope }))).data,
    onSuccess: () => done("Access updated"), onError: onErr,
  });
  const act = useMutation({
    mutationFn: async ({ url, method = "post" }) => (await api[method](url)).data,
    onSuccess: (d, v) => { done(v.msg(d)); setConfirm(null); if (d?.link && v.showLink) { setLinkTitle(v.linkTitle || "Link"); setLink(d.link); } },
    onError: onErr,
  });
  const sentMsg = (what) => (d) => (d.sent_status === "sent" ? `${what} emailed` : `${what} created, but the email could not be sent. Copy the link instead.`);
  const ask = (title, text, action) => setConfirm({ title, text, action });

  return (
    <div>
      <PageHeader title="Center managers" description="Managers see employees and assign existing shifts only for their locations or departments"
        actions={<Button onClick={() => setDialog("invite")} data-testid="invite-manager-btn"><UserPlus className="mr-2 h-4 w-4" /> Invite manager</Button>} />

      <Card className="divide-y divide-slate-100">
        {data?.active?.map((m) => (
          <div key={m.membership_id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3" data-testid={`manager-${m.email}`}>
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600"><MapPinned className="h-4 w-4" /></div>
              <div>
                <p className="text-sm font-medium text-slate-800">{m.name}</p><p className="text-xs text-slate-400">{m.email}</p>
                <div className="mt-1 flex flex-wrap gap-1">{m.scope.map((s, i) => <span key={i} className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{scopeLabel(s)}</span>)}</div>
              </div>
            </div>
            <div className="flex flex-wrap gap-1">
              <Button size="sm" variant="ghost" title="Edit locations and departments" onClick={() => setDialog(m)} data-testid={`edit-manager-${m.email}`}><Pencil className="h-4 w-4" /><span className="ml-1 hidden sm:inline">Edit access</span></Button>
              <Button size="sm" variant="ghost" title="Send password reset link" data-testid={`reset-manager-${m.email}`}
                onClick={() => ask("Send password reset link?", `${m.email} gets an email with a link to set a new password.`, { url: `/org/center-managers/${m.membership_id}/reset-link`, msg: sentMsg("Reset link"), showLink: true, linkTitle: "Password reset link" })}>
                <KeyRound className="h-4 w-4" /><span className="ml-1 hidden sm:inline">Reset password</span></Button>
              <Button size="sm" variant="ghost" title="Delete center manager" data-testid={`remove-manager-${m.email}`}
                onClick={() => ask("Delete this center manager?", `${m.name} (${m.email}) loses access to this organisation's rosters and employees. Rosters they created are kept.`, { url: `/org/center-managers/${m.membership_id}`, method: "delete", msg: () => "Center manager deleted", danger: true })}>
                <Trash2 className="h-4 w-4 text-rose-500" /></Button>
            </div>
          </div>
        ))}
        {!data?.active?.length && <p className="px-5 py-6 text-center text-sm text-slate-400">No center managers yet.</p>}
      </Card>

      {data?.pending?.length > 0 && (
        <>
          <h2 className="mb-2 mt-8 text-sm font-semibold uppercase tracking-wide text-slate-400">Pending invites</h2>
          <Card className="divide-y divide-slate-100">
            {data.pending.map((p) => (
              <div key={p.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-3" data-testid={`pending-manager-${p.email}`}>
                <div><p className="text-sm text-slate-700">{p.email}</p><p className="text-xs text-slate-400">{(p.scope || []).map(scopeLabel).join(", ")}</p></div>
                <div className="flex flex-wrap items-center gap-1">
                  <StatusBadge status={p.sent_status} />
                  <Button size="sm" variant="ghost" title="Edit locations and departments" aria-label="Edit access" onClick={() => setDialog({ ...p, invite_id: p.id })} data-testid={`edit-invite-${p.email}`}><Pencil className="h-4 w-4" /><span className="ml-1 hidden sm:inline">Edit access</span></Button>
                  <Button size="sm" variant="ghost" title="Resend invitation email" aria-label="Resend invite" disabled={act.isPending} data-testid={`resend-invite-${p.email}`}
                    onClick={() => act.mutate({ url: `/org/invitations/${p.id}/resend`, msg: sentMsg("Invitation"), showLink: true, linkTitle: "Invitation link" })}>
                    <Mail className="h-4 w-4" /><span className="ml-1 hidden sm:inline">Resend invite</span></Button>
                  <Button size="sm" variant="ghost" title="Cancel invitation" aria-label="Cancel invite" data-testid={`cancel-invite-${p.email}`}
                    onClick={() => ask("Cancel this invitation?", `The invite link sent to ${p.email} stops working.`, { url: `/org/center-managers/invites/${p.id}`, method: "delete", msg: () => "Invitation cancelled", danger: true, okLabel: "Cancel invite", noLabel: "Keep" })}>
                    <Trash2 className="h-4 w-4 text-rose-500" /></Button>
                </div>
              </div>
            ))}
          </Card>
        </>
      )}

      <Dialog open={!!dialog} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{dialog === "invite" ? "Invite center manager" : "Edit locations and departments"}</DialogTitle>
            <DialogDescription>{dialog === "invite" ? "They will get an email to set a password." : dialog?.email}</DialogDescription>
          </DialogHeader>
          {dialog === "invite" && <div><Label>Email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5" data-testid="manager-invite-email" /></div>}
          <div><Label>Access</Label><div className="mt-1.5"><ScopeEditor scope={scope} setScope={setScope} masters={masters} /></div></div>
          <DialogFooter>
            {dialog === "invite"
              ? <Button disabled={!email || !scopeValid(scope) || invite.isPending} onClick={() => invite.mutate()} data-testid="manager-invite-submit">{invite.isPending ? "Sending…" : "Send invite"}</Button>
              : <Button disabled={!scopeValid(scope) || save.isPending} onClick={() => save.mutate()} data-testid="manager-scope-save">Save</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <LinkDialog open={!!link} onClose={() => setLink("")} title={linkTitle} note="The email was sent if possible. You can also copy and share this link with the center manager." link={link} />
      <Dialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
        <DialogContent className="max-w-md" data-testid="manager-confirm-dialog">
          <DialogHeader><DialogTitle>{confirm?.title}</DialogTitle><DialogDescription>{confirm?.text}</DialogDescription></DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(null)} data-testid="manager-confirm-cancel">{confirm?.action.noLabel || "Cancel"}</Button>
            <Button className={confirm?.action.danger ? "bg-rose-600 hover:bg-rose-700" : ""} disabled={act.isPending} onClick={() => act.mutate(confirm.action)} data-testid="manager-confirm-ok">
              {act.isPending ? "Working…" : confirm?.action.okLabel || (confirm?.action.danger ? "Delete" : "Send")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
